import { MatchmakingMessageType, websocket, campaignApiResponseSchema, ClientType, PresenceMessageType, ProcessLaunchPackage } from '@playcastdotio/Messaging';
import {
  hostMatchmakingState,
  machineIdState,
  hostState,
  matchmakerState,
  hostPresenceState,
  hostInputDominanceState,
  steamInstallState,
  matchmakerOverrideState,
  authState,
  profileState,
  adModeState,
  playjectorHostModeState,
  hostCampaignConfigState,
  hostMacroConfigState,
} from '../../stores/shared';
import { sendMessage } from '../realtime';
import { generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../../messaging/protocols/ipc';
import { getHostGeohash, getCachedGeohash } from '../../../utilities/geolocation';
import * as Sentry from '@sentry/react';
import { attachSentryTraceToMessage } from '../realtime/tracing';
import { getHostCorrelationIds } from '../realtime/hostCorrelation';
import { addObservabilityHeaders } from '@playcastdotio/Observability';
import constants from '@playcastdotio/GlobalConfig';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { campaignSwitchTracker, isTransientCampaignSwitchAbort } from './campaignSwitchTracker';
import config from '../../../config.json';
import { sendLaunchMacro } from './macroManager';
import { stopStream } from '../peers/host';
import { serviceContainer } from '../../../services/ServiceContainer';
import { USE_MESSAGE_ROUTER, USE_SESSION_COORDINATOR, USE_PLAYJECTOR_COORDINATOR } from '../../../config/featureFlags';
import type { ShadowcamHealthSource } from '../../../coordinators/media/managers/ShadowcamPresenceMonitor';
import type { MatchmakeableBlockReason } from '@playcastdotio/Messaging';

// Set by init.ts after MediaCoordinator constructs. Read inside checkShouldBeMatchmaking
// so dead-shadowcam hosts go non-matchmakeable on the legacy code path too.
let shadowcamHealthSource: ShadowcamHealthSource | null = null;
export const setLegacyMatchmakerShadowcamHealthSource = (source: ShadowcamHealthSource): void => {
  shadowcamHealthSource = source;
};

/**
 * Send host matchmaking configuration to realtime API
 * Includes geolocation (geohash + coords) and cloud/wild host type
 */
export const sendMatchmakerHostConfig = async () => {
  // TODO-STRANGLER-FIG: Legacy HOST_CONFIG send - disabled when coordinators active
  if (USE_MESSAGE_ROUTER) {
    console.warn('[Matchmaker] ⛔ Blocked legacy sendMatchmakerHostConfig call (PavilionCoordinator owns this)');
    return;
  }

  return Sentry.startSpan(
    {
      name: 'matchmaking.host_config.send',
      op: 'matchmaking',
      attributes: {
        'matchmaking.machineId': machineIdState.currentState,
        'matchmaking.matchmakeable': hostMatchmakingState.currentState.matchmakeable,
        'matchmaking.globalToggle': hostMatchmakingState.currentState.globalMatchmakingToggle,
        // Guest mode context
        'guest_mode.enabled': hostMatchmakingState.currentState.guestAccount,
        'guest_mode.edition': hostState.currentState.edition,
        'guest_mode.ad_mode': adModeState.currentState.enabled,
        'guest_mode.game_launched': playjectorHostModeState.currentState.gameLaunched,
        'guest_mode.user_logged_in': playjectorHostModeState.currentState.userLoggedIn,
        'guest_mode.steam_launched': playjectorHostModeState.currentState.steamLaunched,
        'guest_mode.host_status': hostState.currentState.status,
      },
    },
    async (span) => {
      // Validate machineId is not empty before proceeding
      if (!machineIdState.currentState || machineIdState.currentState.trim() === '') {
        console.error('[MATCHMAKER] Cannot send host config: machineId is empty or undefined');
        Sentry.captureException(new Error('Attempted to send matchmaker config with empty machineId'), {
          level: 'error',
          tags: {
            issue_type: 'empty_machine_id_matchmaking',
            host_edition: hostState.currentState.edition,
            performance_mode: 'unknown', // Would need to import performanceModeState
          },
          extra: {
            machineId: machineIdState.currentState || '(undefined)',
            hostEdition: hostState.currentState.edition,
          },
        });
        span.setStatus({ code: 2, message: 'Empty machineId' });
        return;
      }

      if (hostState.currentState.edition !== 'pavilion' && hostMatchmakingState.currentState.globalMatchmakingToggle) {
        console.log('Matchmaker not supported in this mode');
        span.setStatus({ code: 2, message: 'Not supported in this mode' }); // 2 = ERROR
        return;
      }
      if (matchmakerState.currentState.state === 'MATCHMAKING_DISABLED' && !hostMatchmakingState.currentState.matchmakeable) {
        console.warn('Matchmaker is disabled, and host is not matchmakeable, so not updating');
        span.setStatus({ code: 2, message: 'Matchmaking disabled' });
        return;
      }

      // Get geolocation (cached or fresh) and update presence
      try {
        // Try to use cached first (fast path)
        const cached = getCachedGeohash();
        let geohash: string;
        let coords: { lat: number; lng: number };

        if (cached) {
          geohash = cached.hash;
          coords = cached.coords;
          console.log('[Matchmaker] Using cached geolocation:', geohash);
        } else {
          // Fetch fresh geolocation (may take up to 10s)
          const result = await getHostGeohash();
          geohash = result.hash;
          coords = result.coords;
          console.log('[Matchmaker] Fetched fresh geolocation:', geohash, `via ${result.method}`);
        }

        const isCloudHost = getCloudHostStatus();

        // Update host presence with geolocation and static host properties
        hostPresenceState.updateState({
          ...hostPresenceState.currentState,
          geohash,
          coords,
          isCloudHost,
        });

        // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
        if (!USE_SESSION_COORDINATOR) {
          // Send PRESENCE_UPDATE with geolocation and static host properties
          console.log('[Matchmaker] Sending PRESENCE_UPDATE with geolocation and host properties...');
          sendMessage(
            websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
              clientType: ClientType.HOST,
              presence: {
                ...hostPresenceState.currentState,
                geohash,
                coords,
                isCloudHost,
              },
            })
          );
        } else {
          console.log('[Matchmaker] ⚠️  Skipping legacy PRESENCE_UPDATE (SessionCoordinator is active)');
        }
      } catch (err) {
        console.warn('[Matchmaker] Geolocation failed, continuing without it:', err);
        // Don't block HOST_CONFIG if geolocation fails
        // Capacity manager can still assign campaigns, matchmaking will be disabled until geo is obtained
      }

      const isCloudHost = getCloudHostStatus();

      const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_HOST_CONFIG, {
        machineId: machineIdState.currentState,
        state: {
          ...hostMatchmakingState.currentState,
          // campaignId, desiredCampaign, gameState, switchRequestedAt, lastSwitchedToCampaign
          // are all managed by hostMatchmakingState and should not be overridden here
          isCloudHost,    // Cloud vs wild host classification
          // NOTE: Gaming score submitted via SUBMIT_SCORING_METRICS (server-computed)
        },
      });

      // Attach Sentry trace context to the message for distributed tracing
      attachSentryTraceToMessage(msg);
      sendMessage(msg);
      span.setStatus({ code: 1 }); // 1 = OK
    }
  );
};

/**
 * Get cloud host status from presence state.
 * This value is determined server-side during auth via cloudHostToken validation.
 */
function getCloudHostStatus(): boolean {
  return hostPresenceState.currentState.isCloudHost || false;
}

export const sendHostReady = () => {
  return Sentry.startSpan(
    {
      name: 'matchmaking.host_ready.send',
      op: 'matchmaking',
      attributes: {
        'matchmaking.invitationId': matchmakerState.currentState.stateDetails?.currentMatchInvitationId || 'unknown',
        'matchmaking.hostPeerId': hostPresenceState.currentState.hostPeerId || 'unknown',
        // Guest mode context
        'guest_mode.enabled': hostMatchmakingState.currentState.guestAccount,
        'guest_mode.edition': hostState.currentState.edition,
        'guest_mode.ad_mode': adModeState.currentState.enabled,
        'guest_mode.game_launched': playjectorHostModeState.currentState.gameLaunched,
        'guest_mode.user_logged_in': playjectorHostModeState.currentState.userLoggedIn,
      },
    },
    (span) => {
      if (!hostPresenceState.currentState.hostPeerId) {
        console.error('Host peer ID is not set');
        span.setStatus({ code: 2, message: 'Host peer ID not set' });
        return;
      }
      if (!matchmakerState.currentState.stateDetails?.currentMatchInvitationId) {
        console.error('Invitation ID is not set');
        span.setStatus({ code: 2, message: 'Invitation ID not set' });
        return;
      }

      const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_HOST_READY, {
        invitationId: matchmakerState.currentState.stateDetails?.currentMatchInvitationId,
        hostPeerId: hostPresenceState.currentState.hostPeerId,
      });

      // Attach Sentry trace context to the message for distributed tracing
      attachSentryTraceToMessage(msg);
      sendMessage(msg);
      span.setStatus({ code: 1 });
    }
  );
};

const checkShouldBeMatchmaking = (): {
  matchmakeable: boolean;
  reason: string;
  blockReason?: MatchmakeableBlockReason | null;
} => {
  // Base criteria that must be met for all modes:
  // 1. Must be in pavilion mode
  // 2. Must have override enabled (guest account or manual override)
  // 3. Must not already be matchmade
  // 4. Host must not have input dominance
  //
  // Then check mode-specific requirements:
  // - Ad Mode: Game must be launched
  // - Normal Mode: Steam must be installed

  // Shadowcam gate runs first so its admin-visible reason wins over the generic "not ready" fallback.
  const shadowcamHealth = shadowcamHealthSource?.getHealth();
  if (shadowcamHealth && !shadowcamHealth.healthy) {
    console.warn('[Matchmaker] FAILED: Shadowcams unhealthy, marking as not matchmakeable', shadowcamHealth);
    return { matchmakeable: false, reason: 'shadowcam_unhealthy', blockReason: 'shadowcam_unhealthy' };
  }

  // Check 1: Must be in pavilion edition
  const edition = hostState.currentState.edition;
  console.log('[Matchmaker] checkShouldBeMatchmaking - edition check:', {
    edition,
    guestAccount: hostMatchmakingState.currentState.guestAccount,
    steamInstall: steamInstallState.currentState.steamInstall,
    adModeEnabled: adModeState.currentState.enabled,
  });
  if (edition !== 'pavilion') {
    return { matchmakeable: false, reason: 'not_pavilion_edition' };
  }

  // Check 2: Must have override enabled (guest account or manual override)
  const guestAccount = hostMatchmakingState.currentState.guestAccount;
  const override = matchmakerOverrideState.currentState;
  if (!guestAccount && !override) {
    return { matchmakeable: false, reason: 'no_guest_account_or_override' };
  }

  // Check 3: Must have global matchmaking toggle enabled
  const hostOverride = hostMatchmakingState.currentState.globalMatchmakingToggle;
  if (!hostOverride) {
    return { matchmakeable: false, reason: 'global_matchmaking_toggle_disabled' };
  }

  // Check 4: Must not already be matchmade
  const matchmade =
    matchmakerState.currentState.state === 'MATCHMAKING_DISABLED' && matchmakerState.currentState.stateDetails?.currentMatchInvitationId;
  if (matchmade) {
    return { matchmakeable: false, reason: 'already_matched' };
  }

  // Check 5: Host must not have input dominance
  // const inputDominance = hostInputDominanceState.currentState;
  // const doesHostHaveInputDominance = Object.values(inputDominance).some((value) => value === true);
  const doesHostHaveInputDominance = false;
  if (doesHostHaveInputDominance) {
    return { matchmakeable: false, reason: 'host_has_input_dominance' };
  }

  // Check mode-specific requirements
  const isAdMode = adModeState.currentState.enabled;

  if (isAdMode) {
    // Ad Mode: Require game to be launched
    // Playjector is source of truth for gameLaunched state
    const gameLaunched = playjectorHostModeState.currentState.gameLaunched;
    console.log('[Matchmaker] Ad Mode check - gameLaunched:', gameLaunched);
    if (!gameLaunched) {
      console.log('[Ad Mode] Game not launched, not matchmakeable');
      return { matchmakeable: false, reason: 'ad_mode_game_not_launched' };
    }
    console.log('[Ad Mode] All criteria met, matchmakeable');
    return { matchmakeable: true, reason: 'matchmakeable' };
  } else {
    // Normal Mode: Require steam to be installed
    const steamInstall = steamInstallState.currentState.steamInstall;
    console.log('[Matchmaker] Normal Mode (not ad mode) - steamInstall check:', steamInstall);
    if (!steamInstall) {
      console.error('[Matchmaker] FAILED: Steam not installed, marking as not matchmakeable');
      return { matchmakeable: false, reason: 'steam_not_installed' };
    }
    console.log('[Matchmaker] SUCCESS: All criteria met, matchmakeable = true');
    return { matchmakeable: true, reason: 'matchmakeable' };
  }
};

// Track previous matchmakeable state to detect changes
let previousMatchmakeableState: { matchmakeable: boolean; reason: string } | null = null;

// Rate limiting: Track last log time for each state type
let lastMatchmakeableEnabledLogTime = 0;
let lastMatchmakeableDisabledLogTime = 0;
const MIN_LOG_INTERVAL_MS = 60000; // 60 seconds

const updateMatchmakeableState = (trigger: string) => {
  console.log(`[Matchmaker] updateMatchmakeableState triggered by: ${trigger}`);
  const result = checkShouldBeMatchmaking();
  console.log(`[Matchmaker] Result:`, result);

  // Check if state changed
  const stateChanged = previousMatchmakeableState === null ||
      previousMatchmakeableState.matchmakeable !== result.matchmakeable ||
      previousMatchmakeableState.reason !== result.reason;
  console.log(`[Matchmaker] State changed:`, stateChanged, 'previous:', previousMatchmakeableState, 'new:', result);

  if (stateChanged) {
    const eventType = result.matchmakeable ? 'matchmakeable_enabled' : 'matchmakeable_disabled';
    const level = result.matchmakeable ? 'info' : 'warning';
    const now = Date.now();

    // Rate limiting: Check if enough time has passed since last log of this type
    const lastLogTime = result.matchmakeable ? lastMatchmakeableEnabledLogTime : lastMatchmakeableDisabledLogTime;
    const timeSinceLastLog = now - lastLogTime;
    const shouldLog = timeSinceLastLog >= MIN_LOG_INTERVAL_MS ||
                     trigger === 'initialization' || // Always log initialization
                     lastLogTime === 0; // Always log first time

    if (shouldLog) {
      // Update last log time
      if (result.matchmakeable) {
        lastMatchmakeableEnabledLogTime = now;
      } else {
        lastMatchmakeableDisabledLogTime = now;
      }

      // Send full event ONLY on initialization (when ad mode boots up), otherwise breadcrumb
      if (trigger === 'initialization') {
        console.log(`[MATCHMAKING] Host initialization: ${result.matchmakeable ? 'matchmakeable' : 'not matchmakeable'}`);
        Sentry.captureMessage(`[MATCHMAKING] Host initialized - Ad Mode ready`, {
          level: 'info',
          tags: {
            event_type: 'ad_mode_initialized',
            reason: result.reason,
            matchmakeable: String(result.matchmakeable),
            guest_mode: String(hostMatchmakingState.currentState.guestAccount),
            ad_mode: String(adModeState.currentState.enabled),
            edition: hostState.currentState.edition,
          },
          contexts: {
            matchmaking: {
              matchmakeable: result.matchmakeable,
              reason: result.reason,
              trigger,
              machineId: machineIdState.currentState,
            },
            host_config: {
              edition: hostState.currentState.edition,
              globalMatchmakingToggle: hostMatchmakingState.currentState.globalMatchmakingToggle,
              guestAccount: hostMatchmakingState.currentState.guestAccount,
              adModeEnabled: adModeState.currentState.enabled,
              gameLaunched: playjectorHostModeState.currentState.gameLaunched,
              steamInstalled: steamInstallState.currentState.steamInstall,
            },
          },
        });
      } else {
        // Regular state changes - just breadcrumb
        console.log(`[MATCHMAKING] Host ${result.matchmakeable ? 'became' : 'is no longer'} matchmakeable: ${result.reason}`);
        Sentry.addBreadcrumb({
          category: 'matchmaking',
          message: `Host ${result.matchmakeable ? 'became' : 'is no longer'} matchmakeable`,
          level,
          data: {
            event_type: eventType,
            reason: result.reason,
            trigger,
            matchmakeable: result.matchmakeable,
            machineId: machineIdState.currentState,
            guest_mode: hostMatchmakingState.currentState.guestAccount,
            ad_mode: adModeState.currentState.enabled,
            game_launched: playjectorHostModeState.currentState.gameLaunched,
            steam_installed: steamInstallState.currentState.steamInstall,
          },
        });
      }
    } else {
      // Rate limited - log as breadcrumb
      console.log(`[MATCHMAKING] Rate-limited: Host ${result.matchmakeable ? 'became' : 'is no longer'} matchmakeable (${timeSinceLastLog}ms since last log)`);
      Sentry.addBreadcrumb({
        category: 'matchmaking',
        message: `Host ${result.matchmakeable ? 'became' : 'is no longer'} matchmakeable (rate-limited)`,
        level,
        data: {
          event_type: eventType + '_rate_limited',
          reason: result.reason,
          trigger,
          time_since_last_log_ms: timeSinceLastLog,
          guest_mode: hostMatchmakingState.currentState.guestAccount,
          ad_mode: adModeState.currentState.enabled,
        },
      });
    }

    previousMatchmakeableState = result;
  }

  hostMatchmakingState.updateState({
    ...hostMatchmakingState.currentState,
    matchmakeable: result.matchmakeable,
    matchmakeableBlockReason: result.blockReason ?? null,
  });
};

const listeners: Record<string, string> = {};
let recalculationInterval: NodeJS.Timeout | null = null;
let shadowcamHealthUnsubscribe: (() => void) | null = null;

export const startMatchmakerStateMachine = () => {
  // STRANGLER-FIG: Skip legacy matchmaking when new architecture is active
  if (USE_MESSAGE_ROUTER) {
    console.log('[Matchmaker] ⚠️  Skipping legacy matchmaking initialization (USE_MESSAGE_ROUTER=true)');
    console.log('[Matchmaker] PavilionCoordinator owns matchmaking state now');
    return; // Exit early - PavilionCoordinator handles everything
  }

  // Initialize with current state
  updateMatchmakeableState('initialization');

  // CRITICAL: Periodic recalculation to catch edge cases where state machine doesn't trigger
  // This ensures hosts don't get stuck in wrong matchmakeable state
  recalculationInterval = setInterval(() => {
    updateMatchmakeableState('periodic_recalculation');
  }, 10000); // Every 10 seconds
  console.log('[Matchmaker] Started periodic recalculation (every 10s)');

  listeners.hostState = hostState.addListener((state) => {
    updateMatchmakeableState('host_state_change');
  });
  let cachedMatchmakerState = matchmakerState.currentState.state;
  listeners.matchmakerState = matchmakerState.addListener((state) => {
    // When entering MATCHMAKING_DISABLED: recalculate matchmakeable state
    // Don't just blindly set to false - the state machine knows the truth
    if (state.state === 'MATCHMAKING_DISABLED') {
      console.log('[Matchmaker] Received MATCHMAKING_DISABLED, recalculating state');
      updateMatchmakeableState('received_matchmaking_disabled');
    }
    // When leaving MATCHMAKING_DISABLED: recalculate if we should be back in the pool
    else if (cachedMatchmakerState === 'MATCHMAKING_DISABLED') {
      updateMatchmakeableState('left_matchmaking_disabled');
    }
    cachedMatchmakerState = state.state;
  });
  let cachedHostOverride = hostMatchmakingState.currentState.globalMatchmakingToggle;
  let cachedMatchmakeable = hostMatchmakingState.currentState.matchmakeable;
  let cachedCampaignId = hostMatchmakingState.currentState.campaignId;
  let cachedGameState = hostMatchmakingState.currentState.gameState;

  listeners.hostMatchmakingState = hostMatchmakingState.addListener((state) => {
    if (state.globalMatchmakingToggle !== cachedHostOverride) {
      cachedHostOverride = state.globalMatchmakingToggle;
      updateMatchmakeableState('global_toggle_changed');
    }

    // CRITICAL: Send HOST_CONFIG whenever critical state changes
    // This ensures the API always has the latest host state for capacity management
    const matchmakeableChanged = state.matchmakeable !== cachedMatchmakeable;
    const campaignChanged = state.campaignId !== cachedCampaignId;
    const gameStateChanged = state.gameState !== cachedGameState;

    if (matchmakeableChanged || campaignChanged || gameStateChanged) {
      console.log(`[Matchmaker] State changed, sending HOST_CONFIG:`, {
        matchmakeable: matchmakeableChanged ? `${cachedMatchmakeable} -> ${state.matchmakeable}` : 'unchanged',
        campaignId: campaignChanged ? `${cachedCampaignId} -> ${state.campaignId}` : 'unchanged',
        gameState: gameStateChanged ? `${cachedGameState} -> ${state.gameState}` : 'unchanged',
      });

      cachedMatchmakeable = state.matchmakeable;
      cachedCampaignId = state.campaignId;
      cachedGameState = state.gameState;

      sendMatchmakerHostConfig().catch((err) => {
        console.error('[Matchmaker] Failed to send HOST_CONFIG after state change:', err);
      });
    }
  });
  listeners.hostInputDominanceState = hostInputDominanceState.addListener((state) => {
    updateMatchmakeableState('input_dominance_changed');
  });
  listeners.adModeState = adModeState.addListener((state) => {
    updateMatchmakeableState('ad_mode_changed');
  });
  listeners.playjectorHostModeState = playjectorHostModeState.addListener((state) => {
    if(adModeState.currentState.enabled) {
      updateMatchmakeableState('playjector_state_changed');
    }
  });

  if (shadowcamHealthSource) {
    shadowcamHealthUnsubscribe = shadowcamHealthSource.onHealthChange(() => {
      updateMatchmakeableState('shadowcam_health_changed');
    });
  }
};

export const stopMatchmakerStateMachine = () => {
  // Stop periodic recalculation
  if (recalculationInterval) {
    clearInterval(recalculationInterval);
    recalculationInterval = null;
    console.log('[Matchmaker] Stopped periodic recalculation');
  }

  hostState.removeListener(listeners.hostState);
  matchmakerState.removeListener(listeners.matchmakerState);
  hostMatchmakingState.removeListener(listeners.hostMatchmakingState);
  hostInputDominanceState.removeListener(listeners.hostInputDominanceState);
  adModeState.removeListener(listeners.adModeState);
  playjectorHostModeState.removeListener(listeners.playjectorHostModeState);

  shadowcamHealthUnsubscribe?.();
  shadowcamHealthUnsubscribe = null;
};

export const playjectorStartPavilion = () => {
  ipcSend(generators.process.processLaunch(authState.currentState.hostAuthId, 'pavilion', {
    launcher: 'steam',
    readyWaitTimeMs: 30000,
    launchId: '0',
    arguments: '',
    filters: {},
  }));
};

export const playjectorStopPavilion = () => {
  ipcSend(generators.process.processTerminate(authState.currentState.hostAuthId, 'pavilion'));
};

/**
 * Clear the current campaign assignment (go idle)
 * Called when the API disables the current campaign
 *
 * This will:
 * 1. Stop the current game
 * 2. Clear local campaign state
 * 3. Report to API via HOST_CONFIG
 *
 * The capacity manager will then pick up this host as "unassigned"
 * and assign it to an active campaign.
 */
export const clearCampaign = async (reason: string) => {
  console.log(`[Campaign Clear] Clearing campaign assignment (reason: ${reason})`);

  // Safety check: Only allow in pavilion mode
  if (hostState.currentState.edition !== 'pavilion') {
    console.error('[Campaign Clear] Cannot clear - only supported in pavilion mode');
    return false;
  }

  // If we have an active match, kick players and close it first
  if (matchmakerState.currentState.state === 'MATCHMAKING_DISABLED' &&
      matchmakerState.currentState.stateDetails?.currentMatchInvitationId) {
    const invitationId = matchmakerState.currentState.stateDetails.currentMatchInvitationId;
    console.log(`[Campaign Clear] Active match found (${invitationId}), closing it first...`);

    // Send invalidation to API
    sendMessage(
      websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION, {
        invitationId,
        reason: `Campaign disabled: ${reason}`,
      }),
    );

    // Stop stream (kicks all players)
    await stopStream('host:campaign_clear:active_match');
    console.log('[Campaign Clear] Active match closed');
  }

  // Cancel any pending campaign switch
  if (campaignSwitchTracker.hasPendingSwitch()) {
    const pendingCampaign = campaignSwitchTracker.getPendingCampaignId();
    console.log(`[Campaign Clear] Cancelling pending switch to ${pendingCampaign}`);
    campaignSwitchTracker.cancelSwitch();
  }

  const previousCampaignId = hostMatchmakingState.currentState.campaignId;

  try {
    // Stop the current game
    console.log('[Campaign Clear] Stopping current game...');
    ipcSend(generators.process.processTerminate(authState.currentState.hostAuthId, 'pavilion'));

    // Clear local state
    hostMatchmakingState.updateState({
      ...hostMatchmakingState.currentState,
      campaignId: undefined,
      gameState: 'idle',
      matchmakeable: false, // Will be recalculated by state machine
    });

    // Notify API
    console.log('[Campaign Clear] Notifying API of cleared state...');
    await sendMatchmakerHostConfig();

    console.log(`[Campaign Clear] ✅ Successfully cleared campaign (was: ${previousCampaignId})`);
    return true;
  } catch (error) {
    console.error('[Campaign Clear] ❌ Failed to clear campaign:', error);
    Sentry.captureException(error, {
      tags: { event_type: 'campaign_clear_failure' },
      contexts: {
        campaign_clear: {
          previousCampaignId,
          reason,
        },
      },
    });
    return false;
  }
};

export const kickPlayerFromLobby = (playerId: string) => {
  const selectedInvitationId = profileState.currentState.selectedInvitationId;
  if (!selectedInvitationId) {
    console.error('No invitation ID selected');
    return;
  }
  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_KICK_PLAYER, {
    invitationId: selectedInvitationId,
    playerId,
  });
  sendMessage(msg);
};

/**
 * Switch to a different campaign for capacity management
 * Uses the existing PlayjectorService game launch system
 *
 * Assumes all games are pre-installed (December 2025 provisioning)
 */
export const switchToCampaign = async (campaignId: string, reason: string) => {
  console.log(`[Campaign Switch] Switching to campaign ${campaignId} (reason: ${reason})`);

  // Safety check: Only allow campaign switching in pavilion mode
  if (hostState.currentState.edition !== 'pavilion') {
    console.error('[Campaign Switch] Cannot switch - only supported in pavilion mode');
    return false;
  }

  // Safety check: Don't switch if we have an active match
  if (matchmakerState.currentState.state === 'MATCHMAKING_DISABLED' &&
      matchmakerState.currentState.stateDetails?.currentMatchInvitationId) {
    console.error('[Campaign Switch] Cannot switch - host has active match');
    return false;
  }

  // Safety check: Handle pending switch requests
  if (campaignSwitchTracker.hasPendingSwitch()) {
    const pendingCampaign = campaignSwitchTracker.getPendingCampaignId();
    if (pendingCampaign === campaignId) {
      console.warn(`[Campaign Switch] Already switching to ${campaignId} - ignoring duplicate request`);
      return false;
    } else {
      // New switch request for DIFFERENT campaign while one is pending
      console.warn(`[Campaign Switch] Already switching to ${pendingCampaign} - cancelling it to switch to ${campaignId}`);

      // CRITICAL FIX: Cancel the stuck pending switch instead of rejecting
      // This handles the case where:
      // 1. Previous switch failed early (IPC lost, Playjector crashed, etc.)
      // 2. API orchestration state was cleared
      // 3. Host is stuck waiting for a pavilionLaunchReply that will never come
      // 4. New switch arrives but gets rejected because promise is still pending
      //
      // By cancelling here, we allow the API to retry even if the host is stuck
      campaignSwitchTracker.cancelSwitch();
      console.log(`[Campaign Switch] Cancelled pending switch to ${pendingCampaign}, proceeding with ${campaignId}`);

      // Continue with the new switch request (don't return false)
      // The old switch is now cancelled, treat this as a fresh start
    }
  }

  // Capture previous campaign for analytics
  const previousCampaignId = hostMatchmakingState.currentState.campaignId;

  try {
    // Update host state to 'prewarming' (API manages orchestration state)
    hostMatchmakingState.updateState({
      ...hostMatchmakingState.currentState,
      gameState: 'prewarming',
      matchmakeable: false,  // Take out of matchmaking pool during switch
    });

    // Immediately notify API to remove from matchmaking pools
    console.log('[Campaign Switch] Notifying API of prewarming state...');
    await sendMatchmakerHostConfig();

    // Fetch campaign config from realtime-api
    // Uses new campaign management API with Redis storage + hardcoded fallback
    console.log(`[Campaign Switch] Fetching config for ${campaignId}...`);
    const baseUrl = constants.REALTIME_API_BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    const apiUrl = `${baseUrl}/realtime/campaigns/${campaignId}/host-config`;

    const ids = getHostCorrelationIds();
    const headers = addObservabilityHeaders(new Headers(), () => ids.machineId, () => ids.broadcastId);
    const response = await fetch(apiUrl, { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch campaign config: ${response.statusText}`);
    }

    const rawData = await response.json();

    // Validate response with Zod schema
    const data = campaignApiResponseSchema.parse(rawData);

    if (!data.success || !data.campaign) {
      throw new Error(data.error || 'Campaign not found in response');
    }

    const campaignConfig = data.campaign;
    console.log(`[Campaign Switch] Launching ${campaignConfig.displayName}...`);

    // Send analytics event before launching
    serviceContainer.analyticsManager.sendSwitchCampaignRequestedEvent({
      campaignId: campaignId,
      previousCampaignId: previousCampaignId,
      reason: reason,
      gameName: campaignConfig.displayName,
      launcher: campaignConfig.launchPackage.launcher,
    });

    // Start tracking campaign switch with timeout
    // This will be resolved when pavilionLaunchReply is received via IPC handler
    // (`?? 0`: the android launch-package schema relaxed readyWaitTimeMs to
    // optional (Phase 6, E1); this legacy v1 path never sees android packages,
    // so the arithmetic is unchanged for every package it can actually receive.)
    const totalTimeoutMs = config.CAMPAIGN_SWITCH_TIMEOUT + (campaignConfig.launchPackage.readyWaitTimeMs ?? 0);
    console.log(`[Campaign Switch] ⏱️ Setting timeout: ${totalTimeoutMs}ms (base: ${config.CAMPAIGN_SWITCH_TIMEOUT}ms + ready: ${campaignConfig.launchPackage.readyWaitTimeMs}ms)`);

    const launchResultPromise = campaignSwitchTracker.startSwitch(
      campaignId,
      totalTimeoutMs,
    );

    // Launch the campaign's game using PlayjectorService
    // PlayjectorService will handle terminating old game automatically
    console.log('[Campaign Switch] 🎮 Sending process launch request:', {
      campaignId: campaignId,
      displayName: campaignConfig.displayName,
      edition: campaignConfig.edition,
      launchPackage: campaignConfig.launchPackage,
      hostAuthId: authState.currentState.hostAuthId,
      timestamp: Date.now(),
    });

    hostMacroConfigState.updateState({
      onLaunchMacro: campaignConfig.onLaunchMacro,
      onJoinMacro: campaignConfig.onJoinMacro,
      onExitMacro: campaignConfig.onExitMacro ?? [],
      onInactiveMacro: campaignConfig.onInactiveMacro ?? [],
    });

    const launchPackage = campaignConfig.launchPackage;
    if (!launchPackage) {
      throw new Error('No launch package found for campaign');
    }

    // NOTE: For Epic games, the token/username/userId/sandboxId/filePath fields
    // are injected by the Electron backend via injectEpicTokenIfNeeded() in ipcMessaging/index.ts
    // We only need to validate that Epic games have an itemId (the game name)
    if (launchPackage.launcher === 'epic') {
      if (!launchPackage.itemId) {
        throw new Error('Missing required Epic itemId field');
      }
    }

    ipcSend(
      generators.process.processLaunch(
        authState.currentState.hostAuthId,
        campaignConfig.edition,
        launchPackage as ProcessLaunchPackage
      )
    );

    console.log('[Campaign Switch] Waiting for pavilionLaunchReply...');

    // Wait for pavilionLaunchReply (or timeout)
    const launchResult = await launchResultPromise;

    if (launchResult.success) {
      // SUCCESS: Game launched successfully
      console.log('[Campaign Switch] ✅ Game launched successfully');

      // TODO-STRANGLER-FIG: Legacy macro call - disabled when coordinator handles macros
      if (!USE_PLAYJECTOR_COORDINATOR) {
        await sendLaunchMacro();
      }

      // Update host state with new campaign (API manages orchestration state)
      hostMatchmakingState.updateState({
        ...hostMatchmakingState.currentState,
        campaignId: campaignId,
        lastSwitchedToCampaign: Date.now(), // Set completion timestamp (prevents rapid re-switching)
        gameState: 'ready',
        matchmakeable: true,  // Back in matchmaking pool
      });

      // Update campaign config state
      hostCampaignConfigState.updateState({
        ...hostCampaignConfigState.currentState,
        campaignId: campaignId,
        launchPackage: launchPackage as ProcessLaunchPackage,
        restartOnDisconnect: campaignConfig.restartOnDisconnect,
      });

      // Re-register with matchmaker
      console.log('[Campaign Switch] Re-registering with matchmaker...');
      await sendMatchmakerHostConfig();

      // Send success analytics
      serviceContainer.analyticsManager.sendSwitchCampaignSuccessEvent({
        campaignId: campaignId,
        previousCampaignId: previousCampaignId,
        gameName: campaignConfig.displayName,
      });

      console.log(`[Campaign Switch] ✅ Successfully switched to ${campaignConfig.displayName}`);
      return true;

    } else {
      // FAILURE: Game failed to launch or timed out
      const failureReason = launchResult.reason;

      if (isTransientCampaignSwitchAbort(launchResult)) {
        console.warn(
          `[Campaign Switch] Switch to "${campaignId}" aborted (${failureReason}) — likely superseded by another switch; skipping permanent failure / state wipe`,
        );
        return false;
      }

      console.error('[Campaign Switch] ❌ Game launch failed:', failureReason);

      // Report permanent failure to realtime-API
      // Machine name will be resolved by the API from host presence/settings
      // transient: false (or omitted) means this will be recorded to prevent future attempts
      const failureMessage = websocket.generators.matchmaking(
        MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE,
        {
          campaignId,
          reason: failureReason,
          machineName: machineIdState.currentState, // Use machineId as fallback, API will resolve actual name
          transient: false, // Permanent failure - record to prevent retries
        }
      );
      sendMessage(attachSentryTraceToMessage(failureMessage));
      console.log('[Campaign Switch] Sent permanent failure report to realtime-API');

      // Unset campaign (don't revert to previous - let balancer decide)
      // API will clear orchestration state when it receives failure message
      hostMatchmakingState.updateState({
        ...hostMatchmakingState.currentState,
        campaignId: undefined, // Unset campaign
        gameState: 'idle', // No game running
        // Don't set matchmakeable - let state machine calculate it
        // (Ad Mode will be unmatchmakeable until a game launches)
      });

      // Clear campaign config
      hostCampaignConfigState.updateState({
        ...hostCampaignConfigState.currentState,
        campaignId: undefined,
        launchPackage: undefined,
        restartOnDisconnect: undefined,
      });

      // Re-register with matchmaker (now with no campaign)
      console.log('[Campaign Switch] Re-registering with matchmaker (campaign unset)...');
      await sendMatchmakerHostConfig();

      // Send failure analytics
      serviceContainer.analyticsManager.sendSwitchCampaignFailedEvent({
        campaignId: campaignId,
        previousCampaignId: previousCampaignId,
        reason: failureReason,
        gameName: campaignConfig.displayName,
        launcher: campaignConfig.launchPackage.launcher,
      });

      // Log to Sentry
      Sentry.captureMessage(`[Campaign Switch] Failed to launch ${campaignConfig.displayName}`, {
        level: 'warning',
        tags: {
          event_type: 'campaign_switch_failure',
          campaign_id: campaignId,
          reason: failureReason,
        },
        contexts: {
          campaign_switch: {
            campaignId,
            gameName: campaignConfig.displayName,
            reason: failureReason,
            machineId: machineIdState.currentState,
            previousCampaignId,
          },
        },
      });

      return false;
    }

  } catch (error) {
    console.error('[Campaign Switch] ❌ Exception during campaign switch:', error);

    // Cancel pending switch tracking
    campaignSwitchTracker.cancelSwitch();

    // Report permanent failure to realtime-API
    // This ensures capacity manager won't keep retrying this broken campaign
    const errorMessage = error instanceof Error ? error.message : String(error);
    const failureMessage = websocket.generators.matchmaking(
      MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH_FAILURE,
      {
        campaignId,
        reason: `exception_${errorMessage.substring(0, 100)}`, // Truncate for reasonable message size
        machineName: machineIdState.currentState,
        transient: false, // Permanent failure - record to prevent retries
      }
    );
    sendMessage(attachSentryTraceToMessage(failureMessage));
    console.log('[Campaign Switch] Sent permanent failure report to realtime-API (exception)');

    // Unset campaign on error (let balancer decide, API manages orchestration state)
    hostMatchmakingState.updateState({
      ...hostMatchmakingState.currentState,
      campaignId: undefined,
      gameState: 'idle',
      // Don't set matchmakeable - let state machine calculate it
      // (Ad Mode will be unmatchmakeable until a game launches)
    });

    // Clear campaign config
    hostCampaignConfigState.updateState({
      ...hostCampaignConfigState.currentState,
      campaignId: undefined,
      launchPackage: undefined,
      restartOnDisconnect: undefined,
    });

    // Send error state to matchmaker
    console.log('[Campaign Switch] Sending error state to matchmaker...');
    try {
      await sendMatchmakerHostConfig();
    } catch (configError) {
      console.error('[Campaign Switch] Failed to send config to matchmaker:', configError);
    }

    // Log to Sentry
    Sentry.captureException(error, {
      tags: {
        event_type: 'campaign_switch_exception',
        campaign_id: campaignId,
      },
      contexts: {
        campaign_switch: {
          campaignId,
          previousCampaignId,
          machineId: machineIdState.currentState,
        },
      },
    });

    return false;
  }
};
